import { Head, router } from "@inertiajs/react"
import { Badge, Card, Code, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"

import Pagination from "@/components/pagination"
import ProjectLayout from "@/layouts/project-layout"
import {
  projectCampaignPath,
  projectCampaignsPath,
  projectCustomerPath,
  projectDashboardPath,
} from "@/routes"
import { CampaignSchema, type Campaign } from "@/schemas/campaign"
import { CampaignExecutionSchema } from "@/schemas/campaign-execution"
import { ProjectSchema } from "@/schemas/project"

const PropsSchema = z.object({
  project: ProjectSchema,
  campaign: CampaignSchema,
  campaign_executions: z.array(CampaignExecutionSchema),
  current_page: z.number(),
  total_pages: z.number(),
  total_count: z.number(),
})

type Props = z.infer<typeof PropsSchema>

export default function CampaignShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, campaign, campaign_executions, current_page, total_pages, total_count } =
    validatedProps

  const handlePageChange = (page: number) => {
    router.get(projectCampaignPath(project.slug, campaign.id), { page }, { preserveState: true })
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      started: "yellow",
      completed: "green",
      failed: "red",
      cancelled: "gray",
    } as const

    return <Badge color={variants[status as keyof typeof variants] || "gray"}>{status}</Badge>
  }

  const formatSteps = (steps: Campaign["steps"]) => {
    return steps.map((step, index) => {
      if (step.type === "delay") {
        return (
          <div key={index}>
            {index + 1}. Wait {step.duration} {step.unit}
          </div>
        )
      } else if (step.type === "email") {
        return (
          <div key={index}>
            {index + 1}. Send email using template <Code>{step.template}</Code>
          </div>
        )
      } else if (step.type === "push") {
        return (
          <div key={index}>
            {index + 1}. Send push notification <Code>{step.title}</Code>
          </div>
        )
      } else if (step.type === "condition") {
        return (
          <div key={index}>
            {index + 1}. Check if user performed <Code>{step.performed_event}</Code>
            {step.should_exit ? " (exit if true)" : " (continue if true)"}
          </div>
        )
      }
      return null
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Campaigns", href: projectCampaignsPath(project.slug) },
        { title: campaign.name },
      ]}
    >
      <Head title={`${campaign.name} - Campaigns`} />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            {campaign.name}
          </Heading>
        </Flex>

        <Card>
          <Flex direction="column" gap="4">
            <div>
              <Text weight="bold">Description:</Text>
              <Text> {campaign.description}</Text>
            </div>

            <div>
              <Text weight="bold">Trigger:</Text>
              <Text>
                {" "}
                {campaign.trigger.type === "segment_enter" ? "Enter" : "Exit"}{" "}
                <Code>{campaign.trigger.segment}</Code>
              </Text>
            </div>

            <div>
              <Text weight="bold">Steps:</Text>
              <div>{formatSteps(campaign.steps)}</div>
            </div>
          </Flex>
        </Card>

        <Flex justify="between" align="center">
          <Heading size="4" weight="medium">
            Recent Executions ({total_count})
          </Heading>
        </Flex>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Customer</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Progress</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Started At</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {campaign_executions.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={4} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No executions found
                  </Table.Cell>
                </Table.Row>
              ) : (
                campaign_executions.map((execution) => (
                  <Table.Row key={execution.id}>
                    <Table.Cell>
                      <Text>
                        {execution.customer ? (
                          <a
                            href={projectCustomerPath(project.slug, execution.customer.email)}
                            style={{ textDecoration: "none", color: "var(--accent-11)" }}
                          >
                            {execution.customer.email}
                          </a>
                        ) : (
                          <Text color="gray">No customer</Text>
                        )}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>{getStatusBadge(execution.status)}</Table.Cell>
                    <Table.Cell>
                      {execution.current_step_index !== null
                        ? `${execution.current_step_index + 1} / ${execution.total_steps}`
                        : `0 / ${execution.total_steps}`}
                    </Table.Cell>
                    <Table.Cell>{formatDate(execution.created_at)}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>

        <Pagination
          currentPage={current_page}
          totalPages={total_pages}
          onPageChange={handlePageChange}
        />
      </Flex>
    </ProjectLayout>
  )
}
